import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import InvestorLayout from '../../components/InvestorLayout';
import { TrendingUp, DollarSign, Activity, Wallet } from 'lucide-react';

export default function InvestorDashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [recentInvestments, setRecentInvestments] = useState([]);
  const [recentPayouts, setRecentPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [statsRes, investmentsRes, payoutsRes] = await Promise.all([
        axios.get(`${API}/investor/dashboard-stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/investor/investments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/investor/payouts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      setRecentInvestments(investmentsRes.data.slice(0, 5));
      setRecentPayouts(payoutsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <InvestorLayout user={user} onLogout={onLogout} currentPage="dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading...</div>
        </div>
      </InvestorLayout>
    );
  }

  const statCards = [
    {
      title: 'Total Invested',
      value: `₹${(stats?.total_invested || 0).toLocaleString('en-IN')}`,
      icon: DollarSign,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
    },
    {
      title: 'Current Value',
      value: `₹${(stats?.current_value || 0).toLocaleString('en-IN')}`,
      icon: TrendingUp,
      color: 'text-success',
      bgColor: 'bg-success/10',
    },
    {
      title: 'Total Interest',
      value: `₹${(stats?.total_interest || 0).toLocaleString('en-IN')}`,
      icon: Activity,
      color: 'text-warning',
      bgColor: 'bg-warning/10',
    },
    {
      title: 'Total Payouts',
      value: `₹${(stats?.total_payouts || 0).toLocaleString('en-IN')}`,
      icon: Wallet,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
  ];

  return (
    <InvestorLayout user={user} onLogout={onLogout} currentPage="dashboard">
      <div className="space-y-8" data-testid="investor-dashboard">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Welcome back, {user?.full_name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground text-lg">Here's your investment portfolio overview</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="card p-6 stat-card hover:shadow-md"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">{stat.title}</p>
                    <p className="text-2xl font-bold font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {stat.value}
                    </p>
                  </div>
                  <div className={`${stat.bgColor} ${stat.color} p-3 rounded-lg`}>
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <div className="p-6 border-b">
              <h3 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Recent Investments
              </h3>
            </div>
            <div className="p-6">
              {recentInvestments.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No investments yet</p>
              ) : (
                <div className="space-y-4">
                  {recentInvestments.map((inv) => (
                    <div key={inv.investment_id} className="flex justify-between items-center pb-3 border-b last:border-0">
                      <div>
                        <p className="font-mono text-sm text-muted-foreground">{inv.investment_id}</p>
                        <p className="font-semibold">₹{inv.principal_amount.toLocaleString('en-IN')}</p>
                        <p className="text-xs text-muted-foreground capitalize">{inv.interest_type} @ {inv.interest_rate}%</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-semibold text-success">₹{inv.current_value.toLocaleString('en-IN')}</p>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          inv.status === 'active' ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                        }`}>
                          {inv.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="p-6 border-b">
              <h3 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Recent Payouts
              </h3>
            </div>
            <div className="p-6">
              {recentPayouts.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No payouts yet</p>
              ) : (
                <div className="space-y-4">
                  {recentPayouts.map((payout) => (
                    <div key={payout.payout_id} className="flex justify-between items-center pb-3 border-b last:border-0">
                      <div>
                        <p className="font-mono text-sm text-muted-foreground">{payout.payout_id}</p>
                        <p className="text-sm capitalize">{payout.payout_type}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(payout.payout_date).toLocaleDateString('en-IN')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-semibold text-success">₹{payout.amount.toLocaleString('en-IN')}</p>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          payout.status === 'completed' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
                        }`}>
                          {payout.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card p-6">
          <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Quick Stats
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex justify-between items-center pb-3 border-b">
              <span className="text-sm text-muted-foreground">Active Investments</span>
              <span className="font-mono font-semibold text-xl">{stats?.active_investments || 0}</span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b">
              <span className="text-sm text-muted-foreground">Return Rate</span>
              <span className="font-mono font-semibold text-xl text-success">
                {stats?.total_invested > 0 
                  ? ((stats?.total_interest / stats?.total_invested) * 100).toFixed(2)
                  : 0}%
              </span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b">
              <span className="text-sm text-muted-foreground">Growth</span>
              <span className="font-mono font-semibold text-xl text-success">
                ₹{(stats?.total_interest || 0).toLocaleString('en-IN')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </InvestorLayout>
  );
}